import React, { useEffect, useCallback, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { CardHeader, FormControl, MenuItem, OutlinedInput, Select } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import OrderProductsTable from '../OrderProductsTable/OrderProductsTable';
import { formatNumber } from '../../../constants.js/misc';

// Assets
import calendar from '../../../assets/icons/calendar2.svg';
import { useFieldArray, useForm } from 'react-hook-form';
import { parseDateToEdit } from '../../../constants.js/orders';
import OrderProductsTableToEdit from '../OrderProductsTable/OrderProductsTableToEdit';
import { autocompleteUserStoresService } from '../../../services/store';
import { getCouponService } from '../../../services/coupons';
import { DivContainerTable } from '../../../constants.js/containers';
import {
    getOrderLineUnitPrice,
    mergeOrderLineRow,
    enrichOrderLineWithCatalog,
    normalizeOrderLineForSave,
} from '../../../constants.js/orderProductSearch';

const useStyles = makeStyles({
    total: {
        padding: '16px',
        display: 'flex',
        width: '100%',
        justifyContent: 'flex-end',
    },
});

const OrderDetailsTable = ({
    orderDetails,
    order,
    storeData,
    editMode,
    generalData,
    setGeneralData,
    submitProductsTable,
    dataProductsRef,
    getProductsPayloadRef,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const options = { month: 'long', day: 'numeric' };

    const creationDate = new Date(order?.created_at);
    const dateCreation = creationDate.toLocaleDateString('es-ES', options);
    const yearCreation = creationDate.toLocaleDateString('es-ES', { year: 'numeric' });
    const hourCreation = creationDate.toLocaleTimeString();

    const creationDateToEdit = parseDateToEdit(order?.created_at, false);

    const [userStores, setUserStores] = useState([]);
    const [fetchedCouponCode, setFetchedCouponCode] = useState(null);
    const [productCatalog, setProductCatalog] = useState({});
    const fieldRowsRef = useRef([]);
    const productCatalogRef = useRef({});
    const lineMetaByFieldIdRef = useRef({});

    const rememberProducts = useCallback((products) => {
        if (!Array.isArray(products)) return;
        products.forEach((product) => {
            if (product?.id != null) {
                productCatalogRef.current[product.id] = product;
                productCatalogRef.current[String(product.id)] = product;
            }
        });
        setProductCatalog((prev) => {
            const next = { ...prev };
            products.forEach((product) => {
                if (product?.id != null) {
                    next[product.id] = product;
                    next[String(product.id)] = product;
                }
            });
            return next;
        });
    }, []);

    const getCatalogProduct = useCallback((productId) => {
        if (productId == null) return null;
        return (
            productCatalogRef.current[productId] ??
            productCatalogRef.current[String(productId)] ??
            null
        );
    }, []);

    const getDeliveryCost = () => {
        if (!storeData?.delivery_cost_enabled) return 0;
        const raw = storeData?.delivery_cost_details;
        const num = Number(raw);
        return Number.isNaN(num) ? 0 : Math.max(0, num);
    };

    const details = Array.isArray(orderDetails) ? orderDetails : [];
    const productSubtotal = details.reduce(
        (prev, item) => prev + getOrderLineUnitPrice(item) * (Number(item?.quantity) || 0),
        0
    );

    const deliveryCost = getDeliveryCost();
    const storeCurrency = storeData?.currency ?? storeData?.store_plan?.currency ?? '';
    const couponCode = order?.coupon_code ?? order?.coupon?.code ?? fetchedCouponCode ?? null;
    const couponName = order?.coupon_name ?? order?.coupon?.name ?? null;
    const couponDisplay =
        couponName && couponCode
            ? `${couponName} (${couponCode})`
            : couponName || couponCode || '—';
    const couponDiscountRaw =
        order?.coupon_discount_amount ??
        order?.coupon_discount ??
        order?.coupon?.discount_amount ??
        order?.coupon?.discount ??
        order?.applied_coupon?.discount_amount ??
        0;
    const couponDiscount = Math.max(0, Number(couponDiscountRaw) || 0);
    const hasCoupon = !!(couponCode || couponDiscount > 0);
    const grandTotal = Math.max(0, productSubtotal + deliveryCost - couponDiscount);

    useEffect(() => {
        editMode &&
            autocompleteUserStoresService(storeData?.id)
                .then((res) => {
                    setUserStores(res.data);
                })
                .catch((err) => {
                    console.log(err);
                });
    }, [editMode, storeData]);

    useEffect(() => {
        const storeId = storeData?.id;
        const couponId = order?.coupon_id;
        const hasCode = order?.coupon_code ?? order?.coupon?.code;
        if (!storeId || !couponId || hasCode) {
            setFetchedCouponCode(null);
            return;
        }
        getCouponService(storeId, couponId)
            .then((res) => setFetchedCouponCode(res?.data?.code ?? null))
            .catch(() => setFetchedCouponCode(null));
    }, [storeData?.id, order?.coupon_id, order?.coupon_code, order?.coupon?.code]);

    const { register, control, handleSubmit, watch, setValue, getValues } = useForm({
        defaultValues: {
            products: orderDetails?.map((line) => ({
                ...line,
                variants:
                    line?.variants ??
                    line?.order_item_option
                        ?.map((opt) => opt?.product_option_value?.value)
                        .filter(Boolean) ??
                    [],
            })),
        },
        mode: 'all',
    });

    const buildDataProducts = useCallback((submittedProducts, fieldRows = []) => {
        return (submittedProducts ?? []).map((line, index) => {
            const fieldRow = fieldRows[index];
            const meta = fieldRow?.id ? lineMetaByFieldIdRef.current[fieldRow.id] : null;
            const baseRow = mergeOrderLineRow({ ...fieldRow, ...meta }, line);
            const productId = baseRow?.product_id ?? baseRow?.product?.id;
            const catalogProduct =
                getCatalogProduct(productId) ?? meta?.catalogProduct ?? null;
            return normalizeOrderLineForSave(
                enrichOrderLineWithCatalog(baseRow, catalogProduct),
                catalogProduct
            );
        });
    }, [getCatalogProduct]);

    const collectProductsPayload = useCallback(() => {
        const products = getValues('products') ?? [];
        return buildDataProducts(products, fieldRowsRef.current);
    }, [getValues, buildDataProducts]);

    useEffect(() => {
        if (!getProductsPayloadRef) return;
        getProductsPayloadRef.current = collectProductsPayload;
    }, [getProductsPayloadRef, collectProductsPayload]);

    const saveInfo = (data) => {
        const products = watch('products') ?? data?.products ?? [];
        const dataProducts = buildDataProducts(products, fieldRowsRef.current);
        if (dataProductsRef) {
            dataProductsRef.current = dataProducts;
        }
        setGeneralData((prev) => ({
            ...prev,
            CreationDate: watch('newCreationDate'),
            line: watch('line'),
            shippingCost: watch('shipping_cost'),
            dataProducts,
        }));
    };

    return (
        <Card elevation={2} style={{ marginBottom: '21px' }}>
            <CardHeader
                titleTypographyProps={{ variant: 'h6' }}
                title={t('OrderDetail.detailsTitle')}
                style={{ paddingBottom: '0' }}
            />
            <CardContent style={{ padding: 0 }}>
                <Grid container>
                    <div style={{ padding: '10px 16px 16px 16px' }}>
                        {!editMode ? (
                            <>
                                <img src={calendar} alt="calendar" style={{ width: '14px' }} />{' '}
                                {`${dateCreation}, ${yearCreation} - ${hourCreation}`}
                                <Typography variant="body1" color="textSecondary">
                                    Canal de venta:{' '}
                                    <span style={{ fontWeight: 700 }}>
                                        {order?.user_store?.mobile_phone_number}
                                    </span>{' '}
                                </Typography>
                            </>
                        ) : (
                            <>
                                <FormControl
                                    variant="outlined"
                                    margin="normal"
                                    defaultValue={creationDateToEdit}
                                    fullWidth
                                >
                                    <OutlinedInput
                                        defaultValue={creationDateToEdit}
                                        type="datetime-local"
                                        {...register('newCreationDate')}
                                        onMouseOut={() => handleSubmit(saveInfo)()}
                                        fullWidth
                                    />
                                </FormControl>

                                <Select
                                    defaultValue={order?.user_store?.id}
                                    {...register('line')}
                                    onClick={() => handleSubmit(saveInfo)()}
                                    fullWidth
                                    variant="outlined"
                                    disableUnderline
                                >
                                    {userStores?.map((userStore) => (
                                        <MenuItem key={userStore?.id} value={userStore?.id}>
                                            {userStore?.mobile_phone_number}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </>
                        )}
                    </div>
                    {!editMode ? (
                        <>
                            <DivContainerTable>
                                <OrderProductsTable orderDetails={details} storeData={storeData} />
                            </DivContainerTable>
                            <div className={classes.total}>
                                <div style={{ marginRight: '1rem' }}>
                                    <Typography gutterBottom variant="body1">
                                        Subtotal:
                                    </Typography>
                                    <Typography gutterBottom variant="body1">
                                        Costos de envio:
                                    </Typography>
                                    {hasCoupon && (
                                        <>
                                            <Typography gutterBottom variant="body1">
                                                Cupón:
                                            </Typography>
                                            <Typography gutterBottom variant="body1">
                                                Descuento:
                                            </Typography>
                                        </>
                                    )}
                                    <Typography gutterBottom variant="body1">
                                        Total:
                                    </Typography>
                                </div>
                                <div>
                                    <Typography gutterBottom variant="body1">
                                        {formatNumber(productSubtotal, storeCurrency)} {storeCurrency}
                                    </Typography>
                                    <Typography gutterBottom variant="body1">
                                        {formatNumber(deliveryCost, storeCurrency)}
                                    </Typography>
                                    {hasCoupon && (
                                        <>
                                            <Typography gutterBottom variant="body1">
                                                {couponDisplay}
                                            </Typography>
                                            <Typography
                                                gutterBottom
                                                variant="body1"
                                                style={{ color: '#16A34A' }}
                                            >
                                                - {formatNumber(couponDiscount, storeCurrency)}
                                            </Typography>
                                        </>
                                    )}
                                    <Typography gutterBottom variant="body1">
                                        {formatNumber(grandTotal, storeCurrency)} {storeCurrency}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : (
                        <DivContainerTable>
                            <OrderProductsTableToEdit
                                displayBlock
                                deliveryCost={getDeliveryCost()}
                                control={control}
                                register={register}
                                watch={watch}
                                handleSubmit={handleSubmit}
                                setValueShipping={setValue}
                                setFormValue={setValue}
                                useFieldArray={useFieldArray}
                                saveInfo={saveInfo}
                                submitProductsTable={submitProductsTable}
                                rememberProducts={rememberProducts}
                                productCatalog={productCatalog}
                                productCatalogRef={productCatalogRef}
                                lineMetaByFieldIdRef={lineMetaByFieldIdRef}
                                fieldRowsRef={fieldRowsRef}
                            />
                        </DivContainerTable>
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
};

const mapStateToProps = ({ common }) => ({
    storeData: common.storeData,
});

export default connect(mapStateToProps)(OrderDetailsTable);
